/**
 * Google OAuth Button Component
 * @implements FS-001.2.1 - Google OAuth Authentication
 * @implements FS-001.5.2 - Return URL handling after OAuth login
 *
 * A reusable button component that initiates Google OAuth authentication.
 * Redirects to the backend OAuth endpoint which handles state cookie generation
 * and redirects to Google's authorization page.
 */

import { useState } from 'react';
import { Button, CircularProgress, SvgIcon } from '@mui/material';

/**
 * Custom Google "G" SVG icon component
 * MUI does not ship a Google icon, so we create one using the official
 * Google brand multi-color "G" logo.
 * @implements FS-001.2.1 - Google OAuth Authentication
 */
const GoogleIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </SvgIcon>
);

/**
 * Props for the GoogleOAuthButton component
 */
export interface GoogleOAuthButtonProps {
  /** The OAuth flow type - determines button text and backend handling */
  flow: 'login' | 'register_po';
  /** Optional callback fired when authentication starts (before redirect) */
  onAuthStart?: () => void;
  /** Whether the button should be disabled */
  disabled?: boolean;
  /** Invitation code for PO registration flow */
  invitationCode?: string;
  /** Return URL to redirect to after successful authentication */
  returnUrl?: string;
}

/**
 * Google OAuth button that initiates authentication by redirecting to the backend.
 *
 * The button displays:
 * - "Sign in with Google" for login flow
 * - "Continue with Google" for registration flows
 *
 * On click, it redirects to `/auth/google?flow={flow}` which is handled by the
 * backend. The backend generates a state cookie and redirects to Google.
 *
 * @implements FS-001.2.1 - Google OAuth Authentication
 *
 * @example
 * ```tsx
 * // Login flow
 * <GoogleOAuthButton flow="login" />
 *
 * // PO registration with invitation code
 * <GoogleOAuthButton
 *   flow="register_po"
 *   invitationCode="abc123"
 * />
 * ```
 */
export const GoogleOAuthButton = ({
  flow,
  onAuthStart,
  disabled,
  invitationCode,
  returnUrl,
}: GoogleOAuthButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle button click - initiate OAuth flow
   * @implements FS-001.2.1 - Google OAuth redirect to backend
   * @implements FS-001.5.2 - Return URL handling for session expiration flow
   */
  const handleClick = () => {
    setIsLoading(true);
    onAuthStart?.();

    // Build query parameters for the OAuth redirect
    const params = new URLSearchParams({ flow });
    if (invitationCode) {
      params.set('invitationCode', invitationCode);
    }
    // Pass returnUrl to backend so it can redirect after OAuth callback
    if (returnUrl) {
      params.set('returnUrl', returnUrl);
    }

    // Redirect to backend OAuth endpoint
    // Backend handles state cookie generation and redirects to Google
    window.location.href = `/auth/google?${params.toString()}`;
  };

  // Button text varies based on flow type
  const buttonText =
    flow === 'login' ? 'Sign in with Google' : 'Continue with Google';

  return (
    <Button
      variant="contained"
      onClick={handleClick}
      disabled={disabled || isLoading}
      startIcon={
        isLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <GoogleIcon />
        )
      }
      fullWidth
      size="large"
      sx={{
        // Google branding: white background with dark text
        backgroundColor: '#ffffff',
        color: '#3c4043',
        border: '1px solid #dadce0',
        '&:hover': {
          backgroundColor: '#f7f8f8',
          borderColor: '#dadce0',
          boxShadow:
            '0 1px 2px 0 rgba(60,64,67,.30), 0 1px 3px 1px rgba(60,64,67,.15)',
        },
        '&:disabled': {
          backgroundColor: '#f1f3f4',
          color: '#80868b',
          borderColor: '#dadce0',
        },
        // Ensure proper text styling
        textTransform: 'none',
        fontWeight: 500,
      }}
    >
      {isLoading ? 'Redirecting...' : buttonText}
    </Button>
  );
};
